import { useState, useCallback } from 'react';
import { sendChatMessage } from '@/lib/chat';
import { checkRateLimit, getRemainingQuestions } from '@/lib/rate-limit';
import { sanitizeInput, generateSessionId, moderateContent } from '@/lib/security';
import type { ChatMessage } from '@/lib/types/chat';

export function useChatSession() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  const [remainingQuestions, setRemainingQuestions] = useState(30);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (message: string) => {
    try {
      setError(null);
      
      if (!checkRateLimit(sessionId)) {
        setError('Question limit reached. Please try again tomorrow.');
        return;
      }

      const sanitizedMessage = sanitizeInput(message);
      if (!moderateContent(sanitizedMessage)) {
        setError('Invalid input detected. Please try again.');
        return;
      }

      const userMessage: ChatMessage = {
        role: 'user',
        content: sanitizedMessage,
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);
      
      // Get AI response
      const response = await sendChatMessage(sanitizedMessage, {
        messages: messages.map(({ role, content }) => ({ 
          role, 
          content: String(content || '').trim() 
        }))
      });
      
      if (!response) {
        throw new Error('No response received from AI');
      }

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response,
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setRemainingQuestions(getRemainingQuestions(sessionId));

    } catch (error) {
      console.error('Chat error:', error);
      setError(
        error instanceof Error 
          ? error.message 
          : 'Failed to get response. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [messages, sessionId]);

  return {
    session: {
      messages,
      reset: () => setMessages([])
    },
    isLoading,
    error,
    remainingQuestions,
    handleSubmit
  };
}